import React, { useRef } from 'react';
import { useWorkflow } from '../context/WorkflowContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReportExporter from './ReportExporter';

const {
  FiExternalLink, FiAlertTriangle, FiShield,
  FiCheckCircle, FiAlertCircle, FiTarget
} = FiIcons;

/* ── Inline logo used in screen header/footer AND in print ── */
const ReportLogo = ({ forPrint = false }) => (
  <div className={`flex items-center gap-3 ${forPrint ? 'report-logo' : 'mb-1'}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold border
      ${forPrint
        ? 'border-green-500 bg-green-900 text-green-400'
        : 'border-sonar-green/40 bg-sonar-green/10 text-sonar-green'}`}>
      <SafeIcon icon={FiTarget} className="text-lg" />
    </div>
    <div className="flex flex-col leading-none">
      <span className={`font-black text-base tracking-tighter ${forPrint ? 'report-logo-name' : 'text-white'}`}>
        CREDIT<span className={forPrint ? 'report-logo-accent' : 'text-sonar-green'}>SONAR</span>
      </span>
      <span className={`text-[9px] font-mono tracking-[0.2em] uppercase ${forPrint ? 'report-logo-sub' : 'text-slate-500'}`}>
        Intelligence Engine
      </span>
    </div>
  </div>
);

const markdownComponents = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 text-sonar-green hover:text-sonar-green-glow underline underline-offset-2 decoration-sonar-green/40 hover:decoration-sonar-green transition-colors font-mono text-xs break-all"
    >
      {children}
      <SafeIcon icon={FiExternalLink} className="text-[9px] shrink-0 ml-0.5" />
    </a>
  ),
  /* Wrap table in a div that's overflow-x-auto on screen but visible on print */
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-sonar-border shadow-[0_0_15px_rgba(46,204,113,0.05)]">
      <table className="min-w-full">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-sonar-green font-bold text-[10px] uppercase tracking-wider border-b-2 border-sonar-green bg-[#0f3040] font-mono whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-slate-300 border-b border-sonar-border font-mono align-top">
      {children}
    </td>
  ),
  tr: ({ children, ...props }) => (
    <tr className="hover:bg-sonar-green/5 transition-colors" {...props}>{children}</tr>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-sonar-green bg-sonar-green/5 pl-4 py-3 my-4 rounded-r-lg text-slate-300 text-sm">
      {children}
    </blockquote>
  ),
  code: ({ inline, children }) => inline ? (
    <code className="bg-[#0f3040] text-sonar-green px-1.5 py-0.5 rounded border border-sonar-border font-mono text-xs">
      {children}
    </code>
  ) : (
    <pre className="bg-[#050d10] border border-sonar-border rounded-lg p-4 overflow-x-auto my-3">
      <code className="text-[#a0f0c0] font-mono text-xs">{children}</code>
    </pre>
  ),
  hr: () => <hr className="border-sonar-border my-8" />,
  h1: ({ children }) => (
    <h1 className="text-2xl font-black text-white border-b-2 border-sonar-green pb-3 mt-10 mb-5 uppercase tracking-wider font-mono">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-bold text-sonar-green border-l-4 border-sonar-green pl-3 mt-10 mb-4 uppercase tracking-wider font-mono">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-bold text-[#a0f0c0] mt-7 mb-3 font-mono border-b border-sonar-border/50 pb-1">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-bold text-slate-400 uppercase tracking-widest mt-5 mb-2 font-mono">
      {children}
    </h4>
  ),
  ul: ({ children }) => <ul className="space-y-1 my-3 pl-0">{children}</ul>,
  li: ({ children }) => (
    <li className="flex items-start gap-2 text-sm text-slate-300 py-1.5 border-b border-sonar-border/30 font-mono">
      <span className="text-sonar-green font-bold shrink-0 mt-0.5">›</span>
      <span>{children}</span>
    </li>
  ),
  p: ({ children }) => (
    <p className="text-sm text-slate-300 leading-relaxed mb-3 font-mono">{children}</p>
  ),
  strong: ({ children }) => (
    <strong className="font-bold text-white">{children}</strong>
  ),
};

const LONG_FORM_SECTIONS = [
  { id: 1, label: 'Executive Summary' },
  { id: 2, label: 'Entity Profile' },
  { id: 3, label: 'Financial Risk' },
  { id: 4, label: 'Credit Quality' },
  { id: 5, label: 'Sanctions Screen' },
  { id: 6, label: 'Country Risk' },
  { id: 7, label: 'Operational Signals' },
  { id: 8, label: 'Legal & Litigation' },
  { id: 9, label: 'Trade Structuring' },
  { id: 10, label: 'Data Gaps Log' },
  { id: 11, label: 'Final Verdict' },
  { id: 12, label: 'Sources' },
];

const SHORT_FORM_SECTIONS = [
  { id: 1, label: 'Credit Verdict' },
  { id: 2, label: 'Key Financials' },
  { id: 3, label: 'Top Risk Flags' },
  { id: 4, label: 'Credit Terms' },
  { id: 5, label: 'Sources' },
];

const ReportPage = () => {
  const { markdownReport, companyName, location, resetWorkflow, error, reportLayout } = useWorkflow();
  const reportRef = useRef(null);
  const SECTIONS = reportLayout === 'short' ? SHORT_FORM_SECTIONS : LONG_FORM_SECTIONS;

  if (error && !markdownReport) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <SafeIcon icon={FiAlertTriangle} className="text-5xl text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2 uppercase tracking-widest">Generation Failed</h2>
        <p className="text-red-400 font-mono text-sm mb-6">{error}</p>
        <button onClick={resetWorkflow} className="bg-sonar-green text-black px-6 py-3 rounded-lg font-bold uppercase tracking-widest text-sm">
          Try Again
        </button>
      </div>
    );
  }

  if (!markdownReport) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <p className="text-slate-400 font-mono">No report data. Please start a new search.</p>
        <button onClick={resetWorkflow} className="mt-4 bg-sonar-green text-black px-6 py-3 rounded-lg font-bold uppercase tracking-widest text-sm">
          New Search
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">

      {/* ── Screen toolbar ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 no-print">
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className="text-xs font-mono text-sonar-green/50 uppercase tracking-widest">OMEN Trade Credit Memo</span>
            <span className={`text-[9px] border px-2 py-0.5 rounded font-mono uppercase ${
              reportLayout === 'short'
                ? 'bg-blue-500/10 text-blue-400 border-blue-500/30'
                : 'bg-sonar-green/10 text-sonar-green border-sonar-green/30'
            }`}>
              {reportLayout === 'short' ? 'Short Form' : '12 Sections'}
            </span>
            <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded font-mono uppercase">
              Source-Verified
            </span>
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">
            {companyName}
            {location && <span className="text-slate-400 font-normal text-base ml-2">— {location}</span>}
          </h1>
        </div>
        <ReportExporter markdownReport={markdownReport} companyName={companyName} reportRef={reportRef} />
      </div>

      <div className="grid lg:grid-cols-4 gap-8">

        {/* ── TOC Sidebar (screen only) ── */}
        <div className="hidden lg:block no-print">
          <div className="bg-sonar-card border border-sonar-border rounded-2xl p-5 sticky top-28">
            <div className="flex items-center gap-2 mb-1">
              <SafeIcon icon={FiTarget} className="text-sonar-green" />
              <span className="text-xs font-black text-white uppercase tracking-widest">CreditSonar</span>
            </div>
            <p className="text-[9px] text-slate-600 font-mono uppercase tracking-widest mb-4">Intelligence Engine</p>
            <div className="border-t border-sonar-border pt-4">
              <div className="flex items-center gap-2 mb-3">
                <SafeIcon icon={FiShield} className="text-sonar-green text-xs" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                  {reportLayout === 'short' ? 'Short Form' : 'OMEN'} Sections
                </span>
              </div>
              <div className="space-y-1">
                {SECTIONS.map(s => (
                  <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-sonar-green/5 cursor-pointer group transition-colors">
                    <span className="text-[9px] font-black text-sonar-green font-mono w-5 shrink-0">{s.id}.</span>
                    <span className="text-[10px] text-slate-400 group-hover:text-white transition-colors font-mono uppercase tracking-wider">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-6 p-3 bg-sonar-green/5 border border-sonar-green/20 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiCheckCircle} className="text-sonar-green text-xs" />
                <span className="text-[9px] font-bold text-sonar-green uppercase tracking-widest">All Links Verified</span>
              </div>
              <p className="text-[9px] text-slate-500 font-mono leading-relaxed">
                Every factual claim includes a clickable hyperlink for independent verification.
              </p>
            </div>
            {reportLayout === 'long' && (
              <div className="mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiAlertCircle} className="text-amber-400 text-xs" />
                  <span className="text-[9px] font-bold text-amber-400 uppercase tracking-widest">Data Gaps Tagged</span>
                </div>
                <p className="text-[9px] text-slate-500 font-mono leading-relaxed">
                  Missing data is labeled [DATA GAP] with impact assessment throughout.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Report Content ── */}
        {/* print-region wraps everything that should be visible when printing */}
        <div className="lg:col-span-3">
          <div ref={reportRef} className="print-region bg-sonar-card border border-sonar-border rounded-2xl shadow-2xl overflow-hidden">

            {/* ── Top banner ── */}
            <div className="bg-[#050d10] border-b border-sonar-border px-8 py-4 flex items-center justify-between report-print-header">
              <ReportLogo />
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono text-sonar-green/40 uppercase tracking-widest hidden md:block report-header-meta">
                  OMEN v3 // {reportLayout === 'short' ? 'SHORT FORM' : 'FULL MEMO'} // CONFIDENTIAL
                </span>
                <div className="flex gap-1.5 no-print">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-sonar-green/70" />
                </div>
              </div>
            </div>

            {/* ── Markdown body ── */}
            <div className="px-8 md:px-12 py-10 credit-memo">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {markdownReport}
              </ReactMarkdown>
            </div>

            {/* ── Footer ── */}
            <div className="bg-[#050d10] border-t border-sonar-border px-8 py-5 report-print-footer">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <ReportLogo />
                <p className="text-[10px] font-mono text-slate-600 uppercase tracking-widest text-right report-header-meta">
                  OMEN Global Trade Credit Agent v3 •{' '}
                  {new Date().toISOString().slice(0, 19).replace('T', ' ')} UTC
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;